import React, { useEffect, useRef, useState } from 'react';
import jm, { formatDateTime, photoUrl, referredBy, toInputDate } from '../api';
import CandidateTable, { NameCell, RowActions, StatusBadge } from '../common/CandidateTable';
import Modal from '../common/Modal';
import RatingRow from '../common/RatingRow';
import CandidateSummary, {
  SectionHeader,
} from '../common/CandidateSummary';
import useStage from '../common/useStage';
import { EmployeeSelect, useMasters } from '../common/MastersContext';
import DateField from '../common/DateField';
import NumberField from '../common/NumberField';

const NEXT_STATUS = [
  { value: 'Interviewed', label: 'Personal Interview (stay here)' },
  { value: 'Final_interview', label: 'Final Interview' },
  { value: 'hold', label: 'Hold' },
  { value: 'Rejected', label: 'Rejected' },
];

const RATINGS = [
  { name: 'grooming', label: 'Grooming' },
  { name: 'body_language', label: 'Body Language' },
  { name: 'ability_learn', label: 'Ability to Learn' },
  { name: 'communication', label: 'Communication' },
  { name: 'team_player', label: 'Team Player' },
  { name: 'Responsible', label: 'Responsible' },
  { name: 'Dedicated', label: 'Dedicated' },
];

const PersonalInterview = ({ onMoved }) => {
  const masters = useMasters();
  const [form, setForm] = useState({});
  const resumeRef = useRef(null);
  const photoRef = useRef(null);

  const { rows, loading, editing, detail, detailLoading, openEdit, closeEdit, saving, save } =
    useStage(jm.interviews, jm.saveInterview, { onMoved });

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  useEffect(() => {
    if (!detail) return;
    const app = detail.application;
    const stage = detail.interview || {};
    const phone = detail.shortlisted || {};

    setForm({
      first_name: app.first_name || '',
      last_name: app.last_name || '',
      email: app.email || '',
      dob: toInputDate(app.dob),
      contact_no: app.contact_no || '',
      grooming: Number(stage.grooming) || 0,
      body_language: Number(stage.body_language) || 0,
      ability_learn: Number(stage.ability_learn) || 0,
      communication: Number(stage.communication) || 0,
      team_player: Number(stage.team_player) || 0,
      Responsible: Number(stage.Responsible) || 0,
      Dedicated: Number(stage.Dedicated) || 0,
      key_skills: stage.key_skills || '',
      c_salary: stage.c_salary || phone.c_salary || app.current_salary || '',
      e_salary: stage.e_salary || phone.e_salary || app.expected_salary || '',
      interview_date: toInputDate(stage.interview_date),
      interview_time: stage.interview_time || '',
      interview_by: stage.interview_by || '',
      first_report: stage.first_report || '',
      second_report: stage.second_report || '',
      third_report: stage.third_report || '',
      interview_remark: stage.notes || '',
      job_status: 'Interviewed',
      final_notes: '',
      hold_notes: '',
      reject_notes: '',
    });
  }, [detail]);

  const liveScore = RATINGS.reduce((sum, field) => sum + (Number(form[field.name]) || 0), 0);

  const submit = () =>
    save({
      ...form,
      resume_file: resumeRef.current?.files?.[0],
      p_file: photoRef.current?.files?.[0],
    });

  const columns = [
    { key: 'action', label: 'Action', search: false, sortable: false, render: (row) => <RowActions row={row} onEdit={openEdit} /> },
    { key: 'job_title', label: 'Job Title' },
    { key: 'first_name', label: 'Candidate Name', render: (row) => <NameCell row={row} /> },
    { key: 'Referred_others', label: 'Referred By', render: referredBy, sortValue: referredBy },
    { key: 'email', label: 'Email' },
    { key: 'status', label: 'Status', search: false, sortable: false, render: () => <StatusBadge label="Interview" /> },
    { key: 'done_on', label: 'Interviewed On', render: (row) => formatDateTime(row.done_on) },
  ];

  return (
    <>
      <CandidateTable
        title="Personal Interview 1"
        rows={rows}
        columns={columns}
        loading={loading}
        exportName="personal_interview"
      />

      <Modal
        show={!!editing}
        title={editing ? `${editing.first_name} ${editing.last_name || ''}` : ''}
        subtitle={editing ? editing.job_title : ''}
        photo={photoUrl(editing?.emp_img)}
        onClose={closeEdit}
        onSubmit={submit}
        saving={saving}
      >
        {detailLoading && <div className="text-center py-4">Loading…</div>}

        {!detailLoading && detail && (
          <>
            <CandidateSummary candidate={detail.application} />

            <div className="border rounded p-3 mb-3">
              <SectionHeader title="Personal Interview" score={liveScore} max={35} />

              {RATINGS.map((field) => (
                <RatingRow key={field.name} label={field.label} name={field.name} value={form[field.name]} onChange={set} />
              ))}

              <div className="row mt-2">
                <div className="col-md-6 form-group">
                  <label>Current Salary</label>
                  <NumberField value={form.c_salary} onChange={(v) => set('c_salary', v)} />
                </div>
                <div className="col-md-6 form-group">
                  <label>Expected Salary</label>
                  <NumberField value={form.e_salary} onChange={(v) => set('e_salary', v)} />
                </div>
                <div className="col-md-12 form-group">
                  <label>Key Skills</label>
                  <input
                    className="form-control"
                    placeholder="Comma separated, e.g. Tamil, Class Handling"
                    value={form.key_skills || ''}
                    onChange={(e) => set('key_skills', e.target.value)}
                  />
                  <small className="text-muted">Key skills are what the Search tab looks through.</small>
                </div>
              </div>
            </div>

            <div className="border rounded p-3 mb-3">
              <SectionHeader title="Interview Schedule" />
              <div className="row">
                <div className="col-md-6 form-group">
                  <label>Interview Date</label>
                  <DateField value={form.interview_date || ''} onChange={(v) => set('interview_date', v)} />
                </div>
                <div className="col-md-6 form-group">
                  <label>Interview Time</label>
                  <input type="time" className="form-control" value={form.interview_time || ''} onChange={(e) => set('interview_time', e.target.value)} />
                </div>
                <div className="col-md-6 form-group">
                  <label>Interview By</label>
                  <EmployeeSelect value={form.interview_by} onChange={(v) => set('interview_by', v)} employees={masters.employees} />
                </div>
                <div className="col-md-6 form-group">
                  <label>First Level Reporting</label>
                  <EmployeeSelect value={form.first_report} onChange={(v) => set('first_report', v)} employees={masters.employees} />
                </div>
                <div className="col-md-6 form-group">
                  <label>Second Level Reporting</label>
                  <EmployeeSelect value={form.second_report} onChange={(v) => set('second_report', v)} employees={masters.employees} />
                </div>
                <div className="col-md-6 form-group">
                  <label>Third Level Reporting</label>
                  <EmployeeSelect value={form.third_report} onChange={(v) => set('third_report', v)} employees={masters.employees} />
                </div>
                <div className="col-md-12 form-group">
                  <label>Interview Remark</label>
                  <textarea className="form-control" rows="2" value={form.interview_remark || ''} onChange={(e) => set('interview_remark', e.target.value)} />
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6 form-group required-tooltip">
                <label>Job Status</label>
                <select className="form-control required-input" value={form.job_status || ''} onChange={(e) => set('job_status', e.target.value)}>
                  {NEXT_STATUS.map((status) => (
                    <option key={status.value} value={status.value}>{status.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {form.job_status === 'Final_interview' && (
              <div className="form-group">
                <label>Final Interview Notes</label>
                <textarea className="form-control" rows="2" value={form.final_notes || ''} onChange={(e) => set('final_notes', e.target.value)} />
              </div>
            )}

            {form.job_status === 'hold' && (
              <div className="form-group">
                <label>Hold Notes</label>
                <textarea className="form-control" rows="2" value={form.hold_notes || ''} onChange={(e) => set('hold_notes', e.target.value)} />
              </div>
            )}

            {form.job_status === 'Rejected' && (
              <div className="form-group">
                <label>Reject Notes</label>
                <textarea className="form-control" rows="2" value={form.reject_notes || ''} onChange={(e) => set('reject_notes', e.target.value)} />
              </div>
            )}

            <h6 className="mt-4 mb-3">Edit Candidate</h6>
            <div className="row">
              <div className="col-md-6 form-group">
                <label>First Name</label>
                <input className="form-control" value={form.first_name || ''} onChange={(e) => set('first_name', e.target.value)} />
              </div>
              <div className="col-md-6 form-group">
                <label>Last Name</label>
                <input className="form-control" value={form.last_name || ''} onChange={(e) => set('last_name', e.target.value)} />
              </div>
              <div className="col-md-6 form-group">
                <label>Date of Birth</label>
                <DateField value={form.dob || ''} onChange={(v) => set('dob', v)} />
              </div>
              <div className="col-md-6 form-group">
                <label>Contact Number</label>
                <input className="form-control" maxLength={10} value={form.contact_no || ''} onChange={(e) => set('contact_no', e.target.value)} />
              </div>
              <div className="col-md-6 form-group">
                <label>Email</label>
                <input className="form-control" value={form.email || ''} onChange={(e) => set('email', e.target.value)} />
              </div>
              <div className="col-md-6 form-group">
                <label>Resume</label>
                <input type="file" className="form-control-file" ref={resumeRef} accept=".pdf,.doc,.docx" />
              </div>
              <div className="col-md-6 form-group">
                <label>Photo</label>
                <input type="file" className="form-control-file" ref={photoRef} accept="image/*" />
              </div>
            </div>
          </>
        )}
      </Modal>
    </>
  );
};

export default PersonalInterview;
